// constants
import { WIDGET_RC_PDP_BANNER_WIDGET } from 'shared/layouts/DetailsPage/constants';

import {
  DEFAULT_PRODUCT_INFO,
  HYBRID_PDP_WIDGETS,
  HYBRID_PDP_WIDGET_TITLES,
} from './constants';

// defs
import { TransformedProductInfo, TransformedWidgetInfo } from './types';

type WidgetAlgoConfigValue = { algo?: string } | string;
type WidgetAlgoConfigItem = { [key: string]: WidgetAlgoConfigValue };

export const getRecommendedProducts = <T extends Record<string, unknown>>(
  products: T[],
  landingProductId: string
): T[] => {
  const seen = new Set<string>([String(landingProductId)]);

  if (!Array.isArray(products)) return [];

  return products.filter((product) => {
    const id =
      product &&
      (product.id || product.product_id || product.productId ||
        product.productID || product.parentId || product.sku);
    if (!id || seen.has(String(id))) return false;
    seen.add(String(id));
    return true;
  });
};

export const getProductAnalyticsMetadata = (product: Record<string, unknown>) => {
  const categories = product.primary_categories_json_all;
  const categoryLevels: Record<string, { id: string; name: string }> = {};
  if (categories && typeof categories === 'object' && !Array.isArray(categories)) {
    Object.keys(categories).forEach((key) => {
      const category = (categories as Record<string, unknown>)[key];
      if (!category || typeof category !== 'object') return;
      const value = category as Record<string, unknown>;
      if (typeof value.name !== 'string') return;
      categoryLevels[key] = { name: value.name, id: String(value.id || '') };
    });
  }
  return { categoryLevels, division: typeof product.division === 'string' ? product.division : undefined };
};

export const transformProductInfo = (detailsAPIResponse: any): TransformedProductInfo => {
  try {
    const {
      title = '',
      subTitle = '',
      tag_list = [],
      sizeOptions,
      sizeGuideURL = '',
      productMedia = [],
      imageUrl,
      return_period: returnPeriod = 0,
      review_rating_json = { show_ratings: true, star_rating: '', star_rating_count: '' },
      price,
      discountedPrice,
      discount = 0,
      type,
      action_url: actionURL,
      inventory_nudge: inventoryNudge,
      isOutOfStock,
      id,
      sku,
    } = detailsAPIResponse;
    return {
      ...DEFAULT_PRODUCT_INFO,
      deliveryInfo: returnPeriod ? `${returnPeriod} days return & exchange` : '',
      images: productMedia.length
        ? productMedia.map((item: { url: string }) => item.url)
        : [imageUrl],
      priceInfo: {
        originalPrice: price,
        discountedPrice: discountedPrice,
        discount,
      },
      ratingsInfo:
        review_rating_json?.show_ratings === false || review_rating_json?.show_ratings === 'false'
          ? {
              rating: '',
              ratingsCount: '',
            }
          : {
              rating: review_rating_json?.star_rating || '',
              ratingsCount: review_rating_json?.star_rating_count || '',
            },
      sizeGuideURL,
      sizeOptions,
      subTitle,
      tags: tag_list?.map((item: { title: string }) => item.title),
      title,
      type,
      actionURL,
      inventoryNudge,
      inStock: Boolean(isOutOfStock === 0),
      id,
      sku,
      analyticsMetadata: {
        ...getProductAnalyticsMetadata(detailsAPIResponse),
        rating: review_rating_json?.star_rating,
        ratingsCount: review_rating_json?.star_rating_count,
      },
    };
  } catch (error) {
    return DEFAULT_PRODUCT_INFO;
  }
};

export const transformWidgetInfo = (
  widgetsApiResponse: any = {},
  pageNumber = 1
): TransformedWidgetInfo => ({
  fetchMoreProducts: widgetsApiResponse?.has_more || false,
  products: widgetsApiResponse?.products || [],
  pageNumber,
  title: widgetsApiResponse?.title || '',
  actionURL: widgetsApiResponse?.action_url || '',
  widgetType: widgetsApiResponse?.widget_type || '',
  showViewAll: widgetsApiResponse?.show_view_all || false,
});

export const getWidgetTitle = (widgetType: string, apiTitle = '') => {
  if (apiTitle) {
    return apiTitle;
  }

  const widgetConfig = HYBRID_PDP_WIDGETS.find((widget) => widget.type === widgetType);

  return widgetConfig?.title || HYBRID_PDP_WIDGET_TITLES[widgetType] || '';
};

export const getWidgetAlgo = (
  widgetAlgoConfig: WidgetAlgoConfigItem[],
  widgetType: string
) => {
  let algo = '';

  try {
    widgetAlgoConfig.forEach((widgetAlgoItem) => {
      if (!widgetAlgoItem) {
        return;
      }

      if (Object.prototype.hasOwnProperty.call(widgetAlgoItem, widgetType)) {
        const widgetConfig = widgetAlgoItem[widgetType];
        algo = typeof widgetConfig === 'string' ? widgetConfig : widgetConfig?.algo || '';
      }
    });
  } catch (error) {
    // do nothing
  }

  return algo;
};

export const getRecommendationWidgetTypesFromConfig = (
  widgetAlgoConfig: WidgetAlgoConfigItem[] = []
) => {
  try {
    return widgetAlgoConfig
      .map((widgetAlgoItem) => Object.keys(widgetAlgoItem || {})[0])
      .filter((widgetType) => widgetType && widgetType !== WIDGET_RC_PDP_BANNER_WIDGET);
  } catch (error) {
    return [];
  }
};

export const canFetchWidgetDetails = ({ type, algo }: { type?: string; algo?: string | null }) =>
  Boolean(type) && algo !== undefined && algo !== null;
